"use client";
import React, { useEffect, useState } from 'react';
import { useActiveBranch } from '@sourcepilot/ui/framework/data/active-branch';
import { versioningClient } from '@sourcepilot/ui/framework/data/versioning-client';
import { NOTED_WIRE } from '@sourcepilot/ui/framework/data/pins/noted-conflicts';
import { ConflictingPinsNote } from '@sourcepilot/ui/framework/ui/molecules/NotedConflicts';

type Snapshot = { status: string; code?: string; reason?: string; result?: unknown; selected?: Record<string,string>; inputs?: Array<{id:string;attribute:string;value:unknown}>; derived?: Array<{ id: string; attribute: string; value: unknown; expression?: string; context: { commit_id?: string } }> };

export function CalculationStateView({ holder, expression, stale, epoch, calculationStatus }: {
  holder?: string; expression?: string; stale: boolean; epoch?: number; calculationStatus?: string;
}) {
  const active = useActiveBranch();
  const lane = active && active.islandId === holder ? active.branch : 'main';
  const [loaded, setLoaded] = useState<{ key: string; snapshot: Snapshot } | null>(null);
  const [refresh, setRefresh] = useState(0);
  const key = JSON.stringify([holder, expression, lane, epoch, refresh]);
  useEffect(() => {
    let cancelled = false;
    if (!holder || !expression || stale) return;
    versioningClient.calculationState(holder, expression, lane).then(snapshot => {
      if (!cancelled) setLoaded({key, snapshot});
    }).catch(() => { if (!cancelled) setLoaded({key, snapshot:{status:'unavailable'}}); });
    return () => { cancelled = true; };
  }, [holder, expression, lane, stale, key]);
  if (!holder || !expression) return null;
  const snapshot = loaded?.key === key ? loaded.snapshot : null;
  return <section className="mt-3 border-t pt-2 text-xs" aria-label="Calculation dependencies">
    <div className="flex justify-between"><strong>Calculation dependencies</strong>
      <button type="button" onClick={() => setRefresh(x => x + 1)}>Refresh</button></div>
    {calculationStatus === 'timeout' ? <p>Still calculating. The calculation continues.</p>
      : calculationStatus === 'unavailable' ? <p>Calculation state is unavailable.</p>
      : calculationStatus === 'error' ? <p>Calculation failed. No current dependency result is available.</p>
      : stale || snapshot?.status === 'stale' ? <p>Inputs changed. Dependency values will be available after recalculation.</p>
      : !snapshot ? <p>Loading calculation state…</p>
      // V33 revised (VC-D): reads through a conflict only noted in this
      // draft (or, under its own code, a link a kept override breaks) —
      // incomplete, in the server's sentence, with no saved values
      : snapshot.status === NOTED_WIRE.calculationStatus
        ? <div data-testid="calculation-conflicting-pins" data-code={snapshot.code}><p className="font-medium text-amber-800">
            {snapshot.code === NOTED_WIRE.calculationCode ? 'Incomplete: conflicting pins' : 'Incomplete'}</p>
            <ConflictingPinsNote sentences={snapshot.reason ? [snapshot.reason] : []} testId="calculation-conflicting-pins-reason" /></div>
      : snapshot.status === 'current' ? <div><p>Result: {snapshot.result == null ? '—' : JSON.stringify(snapshot.result)}</p><ul>{snapshot.derived?.map((d, i) => <li key={`${d.id}-${i}`} className="mt-2">
          <span>{d.attribute}: {d.value == null ? '—' : JSON.stringify(d.value)}</span>
          {d.context.commit_id && <div className="text-gray-500">Selected version {d.context.commit_id.slice(0,10)}</div>}
          {d.expression && <code className="break-words">{d.expression}</code>}
        </li>)}</ul>
        {!!snapshot.inputs?.length && <details className="mt-2"><summary>Authored inputs</summary><ul>
          {snapshot.inputs.map((input, i) => <li key={`${input.id}-${i}`}>{input.attribute}: {JSON.stringify(input.value)}</li>)}
        </ul></details>}
        {!!snapshot.selected && <details className="mt-2"><summary>Selected versions</summary><ul>
          {Object.entries(snapshot.selected).map(([island, commit]) => <li key={island} className="break-all">{island}: {commit}</li>)}
        </ul></details>}
      </div>
      : <p>{snapshot.status === 'missing' ? 'No retained dependency calculation yet.' : 'Calculation state is unavailable. Its inputs or access may have changed.'}</p>}
  </section>;
}
